import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { styled } from '@mui/material/styles'
import { pick } from 'ramda'
import React from 'react'

import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog-header'
import { ContactGroup } from '@/app/module/contacts/types'
import { ContactAccess, UserType } from '@/app/module/user/types'
import { PersonalizationType, SelectOption, UserRole } from '@/app/types'

import UserAccessContact from './user-access-contact'

type Props = {
  data: UserType
  groups: ContactGroup[]
  groupsLoading: boolean
  loading: boolean
  personalizationList: PersonalizationType

  onClose: () => void
  onGroupsSearch: (search: string) => void
  onSave: (data: UserType) => void
}

const roleOptions: SelectOption<UserRole>[] = [
  {
    label: 'Manager',
    value: 'manager',
  },
  {
    label: 'Collaborator',
    value: 'collaborator',
  },
  {
    label: 'Report Viewer',
    value: 'report_viewer',
  },
  {
    label: 'CATI Agent',
    value: 'cati_agent',
  },
]

/**
 * Manage user role, granular permissions to contact fields, custom fields, and groups
 */
const UserAccessDialog: React.FC<Props> = ({
  data,
  groups,
  groupsLoading,
  loading,
  personalizationList,
  onClose,
  onGroupsSearch,
  onSave,
}) => {
  const [notify, setNotify] = React.useState(data.notify)
  const [role, setRole] = React.useState<UserRole>(data.role)
  const [access, setAccess] = React.useState<ContactAccess>(
    pick(
      ['fullAccess', 'readCustomFieldAll', 'readCustomFieldIds', 'readFields', 'readGroupAll', 'readGroupIds'],
      data,
    ),
  )

  const handleAccessChange = React.useCallback(
    (partial: Partial<ContactAccess>) =>
      setAccess((s) => ({
        ...s,
        ...partial,
      })),
    [],
  )
  const handleSave = () => {
    onSave({ ...data, ...access, notify, role })
  }

  const username = `${data.name} (${data.username})`

  return (
    <StyledDialog aria-labelledby="user-access-title" fullWidth open={true} onClose={onClose}>
      <DialogHeader
        title={
          <>
            Manage user <b>{username}</b>
          </>
        }
        onClose={onClose}
      />
      <DialogContent>
        <FormContainer>
          <FullWidthFormControl>
            <FormLabel htmlFor="role">Role</FormLabel>
            <Select
              data-testid="role-select"
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
            >
              {roleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FullWidthFormControl>
          <FormControlLabel
            control={<Checkbox />}
            checked={notify}
            data-testid="set-notify"
            label="This user should receive account notifications like recurring charge emails, low balance notifications, etc."
            name="set-notify"
            onChange={() => setNotify((s) => !s)}
          />
          <UserAccessContact
            access={access}
            groups={groups}
            groupsLoading={groupsLoading}
            personalizationList={personalizationList}
            username={username}
            onChange={handleAccessChange}
            onGroupsSearch={onGroupsSearch}
          />
        </FormContainer>
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button color="primary" loading={loading} variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiPaper-root': {
    height: '100%',
  },
}))

const FormContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
}))

const FullWidthFormControl = styled(FormControl)(() => ({
  width: '100%',
}))

export default UserAccessDialog
